import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  type AdvancedBookingTier,
  type AdvancedBookingParams,
  type RuleFormProps,
  pctStrToNumOpt,
  intStrOpt,
} from '@/lib/pricingRules';

// advanced_booking: tiered lead-time discount/markup for far-out bookings.
// Each tier fires when the booking's lead time is >= beyond_days; the
// TIGHTEST matching tier wins (largest beyond_days the lead time still
// clears). Mirrors the entry/tier-list shape from DateSpecificRuleForm (the
// reference form): local string-draft state seeded once from params,
// rebuilt + emitted on every change. Structurally identical to
// LastMinuteRuleForm except for the threshold field name/direction.
//
// beyond_days and pct are REQUIRED by the backend (no omitempty) — blank/
// invalid input falls back rather than being omitted: beyond_days keeps the
// last valid value (or 1), pct coerces to 0. min_stay is optional and is
// ALWAYS emitted explicitly (`null` when blank — the backend's `*int` field
// has no omitempty and expects the key present).

interface TierDraft {
  beyondDays: string;
  beyondDaysNum: number; // last-known-valid beyond_days (>= 1); used on emit
  pct: string;
  minStay: string;
}

function tierToDraft(t: AdvancedBookingTier): TierDraft {
  return {
    beyondDays: String(t.beyond_days),
    beyondDaysNum: t.beyond_days,
    pct: String(t.pct),
    minStay: t.min_stay != null ? String(t.min_stay) : '',
  };
}

function draftToTier(d: TierDraft): AdvancedBookingTier {
  const p = pctStrToNumOpt(d.pct);
  const pct = typeof p === 'number' ? p : 0;
  const ms = intStrOpt(d.minStay);
  const min_stay = typeof ms === 'number' ? ms : null;
  return { beyond_days: d.beyondDaysNum, pct, min_stay };
}

export function AdvancedBookingRuleForm({ params, onChange }: RuleFormProps<AdvancedBookingParams>) {
  const [drafts, setDrafts] = useState<TierDraft[]>(() => (params.tiers ?? []).map(tierToDraft));

  function commit(next: TierDraft[]) {
    setDrafts(next);
    onChange({ tiers: next.map(draftToTier) });
  }

  function patch(i: number, p: Partial<TierDraft>) {
    commit(drafts.map((d, idx) => (idx === i ? { ...d, ...p } : d)));
  }

  function patchBeyondDays(i: number, value: string) {
    const parsed = intStrOpt(value);
    commit(
      drafts.map((d, idx) => {
        if (idx !== i) return d;
        const num = typeof parsed === 'number' && parsed >= 1 ? parsed : d.beyondDaysNum;
        return { ...d, beyondDays: value, beyondDaysNum: num };
      }),
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {drafts.map((d, i) => (
        <div
          key={i}
          className="rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Booked N+ days ahead"
              type="number"
              min={1}
              value={d.beyondDays}
              onChange={(e) => patchBeyondDays(i, e.target.value)}
              hint="Tightest matching tier wins."
            />
            <Input
              label="Adjustment (%)"
              type="number"
              step="0.1"
              value={d.pct}
              onChange={(e) => patch(i, { pct: e.target.value })}
              placeholder="e.g. -10"
            />
            <Input
              label="Min stay (nights)"
              type="number"
              min={1}
              value={d.minStay}
              onChange={(e) => patch(i, { minStay: e.target.value })}
              placeholder="optional"
            />
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => commit(drafts.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="h-4 w-4" />
              Remove tier
            </Button>
          </div>
        </div>
      ))}

      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            commit([...drafts, { beyondDays: '60', beyondDaysNum: 60, pct: '0', minStay: '' }])
          }
        >
          <Plus className="h-4 w-4" />
          Add tier
        </Button>
      </div>
    </div>
  );
}
